import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { LoadingState } from '@/components/shared/states';
import { AuthLayout } from '@/features/auth/auth-layout';
import { FormError } from '@/features/auth/form-error';
import { api, errorMessage } from '@/lib/api';
import { meKey, needsTermsAcceptance, useMe } from '@/lib/session';
import { ConsentText } from './terms-consent';
import { LEGAL_VERSION } from './legal.config';

/** Tela cheia exibida a quem ainda não aceitou a versão vigente dos Termos e da Política. */
export function AcceptTermsPage() {
  const { data: me, isLoading } = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const accept = useMutation({
    mutationFn: () => api.post('/auth/accept-terms', { version: me?.legalVersion ?? LEGAL_VERSION }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: meKey });
      navigate('/app', { replace: true });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  if (isLoading) return <LoadingState />;
  if (!me) return <Navigate to="/entrar?next=%2Faceitar-termos" replace />;
  if (me.platformAdmin && !me.impersonation) return <Navigate to="/plataforma" replace />;
  if (me.mustChangePassword && !me.impersonation) return <Navigate to="/app/conta" replace />;
  if (!needsTermsAcceptance(me)) return <Navigate to="/app" replace />;

  return (
    <AuthLayout
      title="Termos de Uso e Política de Privacidade"
      subtitle="Atualizamos nossos documentos. Para continuar usando o BarbeiroPro, leia e aceite a versão atual."
    >
      <div className="grid gap-5">
        <FormError message={error} />
        <p className="text-sm leading-relaxed">
          <ConsentText />
        </p>
        <p className="text-xs text-muted-foreground">Versão {me.legalVersion ?? LEGAL_VERSION}. Registramos a versão e a data do seu aceite.</p>
        <Button size="lg" onClick={() => accept.mutate()} disabled={accept.isPending}>
          {accept.isPending ? 'Registrando…' : 'Aceitar e continuar'}
        </Button>
      </div>
    </AuthLayout>
  );
}
